<script>
    import { themeStore, isDarkTheme, themes } from '../../stores/themeStore';
    import { onMount } from 'svelte';
    import ThemeDropdown from './ThemeDropdown.svelte';
    
    let currentTheme = 'light';
    
    onMount(() => {
        // Initialize theme when component mounts
        themeStore.init();
    });
    
    // Subscribe to theme changes
    $: themeStore.subscribe(theme => {
        currentTheme = theme;
    });
    
    function handleToggle() {
        themeStore.toggleTheme();
    }
    
    // Get the appropriate icon and tooltip based on current theme
    function getThemeIcon(theme) {
        switch (theme) {
            case themes.light:
                return 'fa-sun';
            case themes.dark:
                return 'fa-moon';
            case themes.classic:
                return 'fa-pen-nib';  // Classic writing instrument for classic mode
            default:
                return 'fa-sun';
        }
    }
    
    function getThemeTooltip(theme) {
        switch (theme) {
            case themes.light:
                return 'Switch to dark theme';
            case themes.dark:
                return 'Switch to classic theme';
            case themes.classic:
                return 'Switch to light theme';
            default:
                return 'Switch theme';
        }
    }
    
    // Always show dropdown now - user wants dropdown in all modes
    $: showDropdown = true;
</script>

{#if showDropdown}
    <ThemeDropdown />
{:else}
    <button
        class="theme-toggle toolbar-button"
        on:click={handleToggle}
        title={getThemeTooltip(currentTheme)}
        aria-label={getThemeTooltip(currentTheme)}
    >
        <i class="fas {getThemeIcon(currentTheme)}"></i>
    </button>
{/if}

<style>
/* Import toolbar button styles since component is used in toolbar context */
.toolbar-button {
    display: flex;
    justify-content: center;
    align-items: center;
    height: var(--height-toolbar-button);
    padding: var(--spacing-2) var(--spacing-4);
    font-size: var(--font-size-sm);
    white-space: nowrap;
    color: var(--color-text-secondary);
    background-color: var(--color-btn-default);
    border: none;
    border-radius: var(--border-radius-2xl);
    transition: background-color 0.2s var(--ease-in-out);
    cursor: pointer;
    gap: var(--spacing-2);
    box-sizing: border-box;
}

.toolbar-button:hover {
    background-color: var(--color-btn-default-hover);
}

.toolbar-button:active {
    background-color: var(--color-btn-default-active);
}

/* Theme-specific animation */
.theme-toggle:hover i {
    animation: rotate 0.5s ease-in-out;
}

@keyframes rotate {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

/* Responsive design to match toolbar */
@media (width <= 1200px) {
    .toolbar-button {
        padding: var(--spacing-2) var(--spacing-3);
    }
}

@media (width <= 1024px) {
    .toolbar-button {
        padding: var(--spacing-2);
    }
}

@media (width <= 900px) {
    .toolbar-button {
        width: var(--height-toolbar-button-md);
        min-width: var(--height-toolbar-button-md);
        height: var(--height-toolbar-button-md);
        padding: var(--spacing-2);
        border-radius: 50%;
    }
    
    .toolbar-button i {
        margin: 0;
        font-size: var(--font-size-base);
    }
}

@media (width <= 768px) {
    .toolbar-button {
        width: var(--height-toolbar-button-sm);
        min-width: var(--height-toolbar-button-sm);
        height: var(--height-toolbar-button-sm);
    }
}

/* Dark mode text color adjustment */
:global([data-theme="dark"]) .toolbar-button {
    color: var(--color-text-primary);
}
</style>